import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Acessibilidade de modais sem dependência externa:
 * - foca o primeiro elemento focável ao abrir
 * - mantém o foco preso dentro do container (Tab no último volta pro primeiro, Shift+Tab no primeiro volta pro último)
 * - chama `onEscape` quando Esc é pressionado (use pra fechar o modal)
 *
 * <p>Quando `active=false`, listeners são removidos — usar com o mesmo flag
 * que controla a visibilidade do dialog (`isOpen`).</p>
 *
 * <p>Não restaura foco no elemento anterior ao fechar (Outline pequeno
 * — adicionar se aparecer necessidade real; padrão browser já cobre a maior parte).</p>
 */
export function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  active: boolean,
  onEscape?: () => void
) {
  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;

    // microtask pra garantir que o DOM já renderizou (componente pode estar
    // montando concomitante com active=true).
    const focusFirst = () => {
      const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const first = Array.from(focusables).find(el => el.offsetParent !== null);
      first?.focus();
    };
    queueMicrotask(focusFirst);

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) {
        e.preventDefault();
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(el => el.offsetParent !== null);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [active, onEscape, ref]);
}
